import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  PlusOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  ExpandOutlined,
  ReloadOutlined,
  SwapOutlined,
  RobotOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  DeploymentUnitOutlined,
} from "@ant-design/icons";
import WorkflowNode from "./WorkflowNode";
import WorkflowConnector from "./WorkflowConnector";
import {
  NODE_W,
  NODE_H,
  pointFor,
  boundsFor,
  bezierBetween,
  rightAnchorAt,
  leftAnchorAt,
  edgeMidpointAt,
  layoutTree,
  fitView,
  clampZoom,
} from "../../helper/masterSuiteCanvas";
import { descendantsOf } from "../../helper/masterSuiteTree";
import "../../styles/workflow.css";

// Empty-state tiles — first click adds a step AND teaches what a suite can hold.
const PRESET_TILES = [
  { label: "Reconciliation", icon: <SwapOutlined />, preset: { type: "Task", category: "compare" } },
  { label: "UI Test", icon: <RobotOutlined />, preset: { type: "Task", category: "ui" } },
  { label: "Event Monitor", icon: <EyeOutlined />, preset: { type: "Task", category: "process", subcategory: "eventMonitor" } },
  { label: "Process Automation", icon: <ThunderboltOutlined />, preset: { type: "Task", category: "process" } },
  { label: "Master Suite", icon: <DeploymentUnitOutlined />, preset: { type: "Master Suite" } },
];

/**
 * A node in edit mode: draggable AND a drop target, because a drag re-parents.
 *
 * Positions are computed from the tree, so dragging a node to a new PLACE would be
 * meaningless — the next layout pass would put it straight back. What a drag means
 * here is "make this step run after the one I dropped it on"; the layout then follows
 * from the new structure. Sibling ORDER is still changed from the Table view and the
 * drawer's move up/down.
 *
 * The dnd-kit hooks live here rather than in WorkflowNode so view and result modes can
 * render the same card with no DnD context at all.
 */
const DraggableNode = React.memo(function DraggableNode({
  node,
  selected,
  onClick,
  invalid,
  inSubtree,
  dragActive,
  onHover,
}) {
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    isDragging,
  } = useDraggable({ id: node.key });
  // Deliberately NOT disabled for illegal targets. A disabled droppable is not a collision
  // target at all, so dnd-kit reports no `over` and the canvas cannot tell "hovering
  // something you may not drop on" from "hovering empty space" — which makes it impossible
  // to say WHY a drop won't work. Every card is a target; legality is enforced on drop, in
  // handleDragEnd here and again in the editor's handleReparent.
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: node.key });
  const p = pointFor(node);
  const style = {
    position: "absolute",
    left: p.x,
    top: p.y,
    width: NODE_W,
    height: NODE_H,
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 5 : 2,
  };
  return (
    <WorkflowNode
      node={node}
      mode="edit"
      selected={selected}
      isDragging={isDragging}
      // Only signal drop state while something is actually being dragged, so the cards are
      // not permanently tinted. Precedence: the card under the pointer wins, then "travels
      // with the drag", then "illegal" — a descendant is both, and saying it comes along is
      // the more useful of the two.
      dropState={
        !dragActive || isDragging
          ? null
          : isOver
            ? invalid
              ? "reject" // actively hovered but refused — say so loudly
              : "ok"
            : inSubtree
              ? "subtree"
              : invalid
                ? "bad"
                : null
      }
      onClick={onClick}
      onMouseEnter={() => onHover?.(node.key)}
      innerRef={(el) => {
        setDragRef(el);
        setDropRef(el);
      }}
      style={style}
      dragHandleProps={{ ...attributes, ...listeners }}
    />
  );
});

// Alteryx-style workflow canvas: nodes connected by bezier cables on a pannable,
// zoomable surface. One component, three modes: 'edit' | 'view' | 'result'.
function MasterSuiteFlow({
  nodes = [],
  mode: modeProp,
  editable = false, // legacy prop kept for back-compat
  onNodeClick,
  activeKey,
  title = "Workflow",
  subtitle,
  onAddStep,
  onAddPreset,
  onInsertAt,
  // (parentKey) — hover "+" on a card: add a step that runs after it (a new branch).
  onAddChild,
  // (childKey, parentKey) — the canvas drag re-parents rather than reorders.
  onReparent,
}) {
  const mode = modeProp || (editable ? "edit" : "view");
  const isEdit = mode === "edit";
  const n = nodes.length;
  const hasNodes = n > 0;

  const scrollRef = useRef(null);
  const [scale, setScale] = useState(1);
  // Auto-fit is the default and re-runs on structure change and on resize. The moment
  // the user zooms or pans they own the view, and it is never yanked out from under
  // them when the next step is added. "Fit to view" hands control back.
  const [auto, setAuto] = useState(true);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  // Key of the node being dragged, or null. Drives which cards are legal drop targets.
  const [activeId, setActiveId] = useState(null);
  // Live drag position, for the preview cable: which card is under the pointer, and how
  // far the dragged card has travelled.
  const [overId, setOverId] = useState(null);
  const [dragDelta, setDragDelta] = useState(null);
  // Exactly ONE insert affordance is visible at a time. Entering a node clears any hovered
  // cable and vice versa, and leaving the canvas clears both — so a "+" can never linger
  // on top of a card, which is how the old always-on version became clutter.
  const [hoverEdge, setHoverEdge] = useState(null); // `${from.key}->${to.key}`
  const [hoverNode, setHoverNode] = useState(null);
  const clearHover = useCallback(() => {
    setHoverEdge(null);
    setHoverNode(null);
  }, []);
  const hoverEdgeOnly = useCallback((id) => {
    setHoverEdge(id);
    setHoverNode(null);
  }, []);
  // useCallback matters here: this is handed to every DraggableNode, and a fresh function
  // each render would defeat their React.memo — which is the whole point of memoising them.
  const hoverNodeOnly = useCallback((key) => {
    setHoverNode(key);
    setHoverEdge(null);
  }, []);

  // Lay the tree out once, here: both the node layer and the cable layer read the same
  // {depth, lane} so the picture can't drift between them.
  const { nodes: laid, depthCount, maxOffset, edges } = useMemo(
    () => layoutTree(nodes),
    [nodes],
  );

  // "Add step" is NOT a node. It used to occupy a real tree slot, so the layout placed it
  // among the steps — printing its label over a card's title and wedging a ghost between
  // branch children. It is a viewport overlay now, and the stage is sized by real steps
  // alone, so it no longer touches positioning, bounds or auto-fit.
  const { width: stageW, height: stageH } = boundsFor(Math.max(depthCount, 1), maxOffset);
  const bounds = { width: stageW, height: stageH };

  // Track the viewport rather than reading clientWidth ad hoc, so the fit is a pure
  // function of (viewport, bounds) and re-runs for every reason it should: window
  // resize, sidebar collapse, drawer open/close, mode switch.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () =>
      setViewport({ width: el.clientWidth, height: el.clientHeight });
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasNodes, isEdit]);

  // The auto-fit itself. Runs on structure change (stageW/H) and viewport change —
  // but only while the user hasn't taken over.
  useEffect(() => {
    if (!auto || !viewport.width || !viewport.height) return;
    setScale(fitView(viewport, { width: stageW, height: stageH }).scale);
  }, [auto, viewport, stageW, stageH]);

  // Does the tree fit at the current scale? Drives centring and whether panning is on.
  const fits =
    !viewport.width ||
    (stageW * scale <= viewport.width && stageH * scale <= viewport.height);

  const zoomBy = (factor) => {
    setAuto(false);
    setScale((z) => clampZoom(z * factor));
  };
  // Explicitly choosing 100% is a manual decision, not a return to auto.
  const resetZoom = () => {
    setAuto(false);
    setScale(1);
  };
  const fitToView = () => setAuto(true);

  // ctrl/⌘ + wheel → zoom; shift + wheel → horizontal pan; plain wheel → the page.
  // Bound natively with passive:false because React attaches wheel listeners as
  // passive, which makes preventDefault a silent no-op.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        setAuto(false);
        setScale((z) => clampZoom(z * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
        return;
      }
      if (e.shiftKey) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Two-finger pinch → zoom (single-finger pan is native overflow scrolling).
  const pinchRef = useRef(null);
  const onTouchMove = (e) => {
    if (e.touches.length !== 2) return;
    const [a, b] = e.touches;
    const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    if (pinchRef.current != null) {
      const ratio = dist / pinchRef.current;
      setAuto(false);
      setScale((z) => clampZoom(z * ratio));
      e.preventDefault();
    }
    pinchRef.current = dist;
  };
  const onTouchEnd = () => {
    pinchRef.current = null;
  };

  // Drag the empty canvas to pan. Only starts on background: a pointerdown on a node,
  // an insert "+" or the add tile belongs to those controls (and to dnd-kit's sensor).
  const panRef = useRef(null);
  const [panning, setPanning] = useState(false);
  const onPointerDown = (e) => {
    const el = scrollRef.current;
    if (!el || e.button !== 0) return;
    // The floating add button is outside this scroll container, so it never reaches here.
    if (e.target.closest(".ms-canvas-node, .ms-canvas-insert")) return;
    panRef.current = {
      x: e.clientX,
      y: e.clientY,
      left: el.scrollLeft,
      top: el.scrollTop,
    };
    setPanning(true);
    setAuto(false); // the user has chosen where to look
    el.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const el = scrollRef.current;
    const start = panRef.current;
    if (!el || !start) return;
    el.scrollLeft = start.left - (e.clientX - start.x);
    el.scrollTop = start.top - (e.clientY - start.y);
  };
  const endPan = (e) => {
    const el = scrollRef.current;
    if (panRef.current && el) el.releasePointerCapture?.(e.pointerId);
    panRef.current = null;
    setPanning(false);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // Divide the drag delta by the current scale so the ghost tracks the cursor
  // when the stage is zoomed (transform: scale(z)).
  const scaleModifier = useMemo(
    () => ({ transform }) => ({
      ...transform,
      x: transform.x / scale,
      y: transform.y / scale,
    }),
    [scale],
  );

  // Targets a drag may NOT land on: the node itself, and anything already beneath it.
  // Re-parenting under your own descendant is a cycle, and a cycle does not error at
  // run time — the runner simply never schedules any step in it.
  // TWO sets, not one. Descendants are illegal targets AND they travel with the drag, so
  // they read better as "coming along" than as "rejected" — different information.
  const { invalid, subtree } = useMemo(() => {
    if (activeId == null) return { invalid: new Set(), subtree: new Set() };
    const active = nodes.find((n) => n.key === activeId);
    const invalidKeys = new Set([activeId]);
    const subtreeKeys = new Set();
    if (!active?.ref) return { invalid: invalidKeys, subtree: subtreeKeys };
    const banned = descendantsOf(
      nodes.map((n) => ({ stepReference: n.ref, parentId: n.parentId })),
      active.ref,
    );
    nodes.forEach((n) => {
      if (n.ref && banned.has(n.ref)) {
        invalidKeys.add(n.key); // re-parenting under your own descendant is a cycle
        subtreeKeys.add(n.key); // …and it moves with the branch either way
      }
      // The node's CURRENT parent too — dropping there is a no-op, and highlighting a
      // target that does nothing is worse than not offering it.
      if (active.parentId && n.ref === active.parentId) invalidKeys.add(n.key);
    });
    return { invalid: invalidKeys, subtree: subtreeKeys };
  }, [activeId, nodes]);

  const endDrag = () => {
    setActiveId(null);
    setOverId(null);
    setDragDelta(null);
  };

  const handleDragEnd = ({ active, over }) => {
    const illegal = invalid.has(over?.id);
    endDrag();
    // No `over` means empty canvas; `illegal` means a card that would create a cycle (or is
    // the current parent). Either way nothing changes and the node snaps back — its pixel
    // position was never the drag's output, so there is nothing to undo.
    if (over && !illegal && active.id !== over.id) onReparent?.(active.id, over.id);
  };

  const addPreset = (preset) => {
    if (onAddPreset) onAddPreset(preset);
    else if (onAddStep) onAddStep();
  };

  // Read-only + empty renders nothing (keeps view/result pages clean).
  if (!hasNodes && !isEdit) return null;

  const controls = (
    <div className="ms-canvas-controls">
      <button type="button" onClick={() => zoomBy(1 / 1.15)} title="Zoom out">
        <ZoomOutOutlined />
      </button>
      <button type="button" onClick={() => zoomBy(1.15)} title="Zoom in">
        <ZoomInOutlined />
      </button>
      <button
        type="button"
        onClick={fitToView}
        // Lit while auto-fit is on, so the control also reports which mode you're in —
        // otherwise "why did my zoom change when I added a step" has no visible answer.
        className={auto ? "is-active" : undefined}
        aria-pressed={auto}
        title={
          auto
            ? "Fit to view (on — refits as the workflow changes)"
            : "Fit to view"
        }
      >
        <ExpandOutlined />
      </button>
      <button type="button" onClick={resetZoom} title="Reset to 100%">
        <ReloadOutlined />
      </button>
      <span className="ms-canvas-zoomlabel">{Math.round(scale * 100)}%</span>
    </div>
  );

  const stageInner = (
    <>
      <WorkflowConnector
        edges={edges}
        bounds={bounds}
        mode={mode}
        onEdgeHover={isEdit ? hoverEdgeOnly : undefined}
      />

      {/* Insert BETWEEN — HTML above the pointer-events:none SVG, and only for the cable
          actually under the pointer. Both ends are handed over: the parent's index says
          where to splice in the list, the child's key says which step the new one takes
          over from — without it, inserting on a branching step is ambiguous about which
          branch was meant. */}
      {isEdit &&
        activeId == null &&
        edges.map(({ from, to }) => {
          const id = `${from.key}->${to.key}`;
          if (hoverEdge !== id) return null;
          const m = edgeMidpointAt(from, to);
          const at = nodes.findIndex((nd) => nd.key === from.key);
          return (
            <button
              key={`ins-${id}`}
              type="button"
              className="ms-canvas-insert"
              style={{ left: m.x, top: m.y }}
              title="Insert a step here"
              onMouseEnter={() => hoverEdgeOnly(id)}
              onClick={() => onInsertAt?.(at, to.key)}
            >
              <PlusOutlined />
            </button>
          );
        })}

      {/* Add a CHILD — a new branch off the hovered node, matching the fan-out model.
          Sits ON the node's bottom edge rather than below it, so moving the pointer from
          card to "+" never crosses a gap that would drop the hover.
          Suppressed mid-drag: dragging across cards fires mouseenter, and a "+" appearing
          under the cursor competes with the drop target. */}
      {isEdit &&
        activeId == null &&
        hoverNode != null &&
        (() => {
          const node = laid.find((n) => n.key === hoverNode);
          if (!node) return null;
          const p = pointFor(node);
          return (
            <button
              type="button"
              className="ms-canvas-insert ms-canvas-insert--child"
              style={{ left: p.x + NODE_W / 2, top: p.y + NODE_H }}
              title={`Add a step that runs after "${node.title}"`}
              onMouseEnter={() => hoverNodeOnly(hoverNode)}
              onClick={() => onAddChild?.(hoverNode)}
            >
              <PlusOutlined />
            </button>
          );
        })()}

      {/* Drag preview — the cable the drop would create, from the target's exit anchor to
          the card currently in your hand. Same bezier maths as a real connector, so it
          promises exactly what you will get; dashed so it is never mistaken for one.
          Drawn over an ILLEGAL target too, in fail red and ending in an ×: showing why a
          drop will be refused is more use than showing nothing and leaving the user to
          guess whether the canvas even registered the card. */}
      {isEdit &&
        activeId != null &&
        overId != null &&
        overId !== activeId &&
        dragDelta &&
        (() => {
          const target = laid.find((n) => n.key === overId);
          const dragged = laid.find((n) => n.key === activeId);
          if (!target || !dragged) return null;
          const rejected = invalid.has(overId);
          const colour = rejected ? "var(--ms-fail)" : "var(--ms-accent)";
          const b = leftAnchorAt(dragged);
          // delta is screen pixels; the stage is scaled, so divide exactly as the drag
          // modifier does or the cable lags behind the card.
          const tip = {
            x: b.x + dragDelta.x / scale,
            y: b.y + dragDelta.y / scale,
          };
          return (
            <svg
              className="ms-canvas-preview"
              width={stageW}
              height={stageH}
              aria-hidden="true"
            >
              <path
                d={bezierBetween(rightAnchorAt(target), tip)}
                fill="none"
                stroke={colour}
                strokeWidth={2}
                strokeDasharray="6 4"
              />
              {rejected ? (
                <g stroke={colour} strokeWidth={2} strokeLinecap="round">
                  <line x1={tip.x - 4} y1={tip.y - 4} x2={tip.x + 4} y2={tip.y + 4} />
                  <line x1={tip.x - 4} y1={tip.y + 4} x2={tip.x + 4} y2={tip.y - 4} />
                </g>
              ) : (
                <circle cx={tip.x} cy={tip.y} r={3} fill={colour} />
              )}
            </svg>
          );
        })()}

      {isEdit ? (
        <DndContext
          sensors={sensors}
          // pointerWithin, not closestCenter: "drop ONTO this card" is what the gesture
          // means now, so the pointer being inside a card is the right hit test.
          collisionDetection={pointerWithin}
          modifiers={[scaleModifier]}
          onDragStart={({ active }) => {
            setActiveId(active.id);
            clearHover(); // a stale "+" must not sit under the cursor mid-drag
          }}
          onDragOver={({ over }) => setOverId(over?.id ?? null)}
          onDragMove={({ delta }) => setDragDelta(delta)}
          onDragCancel={endDrag}
          onDragEnd={handleDragEnd}
        >
          {laid.map((node) => (
            <DraggableNode
              key={node.key}
              node={node}
              selected={activeKey != null && node.key === activeKey}
              onClick={onNodeClick}
              invalid={invalid.has(node.key)}
              inSubtree={subtree.has(node.key)}
              dragActive={activeId != null}
              onHover={hoverNodeOnly}
            />
          ))}
        </DndContext>
      ) : (
        laid.map((node, i) => {
          const p = pointFor(node);
          return (
            <WorkflowNode
              key={node.key ?? i}
              node={node}
              mode={mode}
              selected={activeKey != null && node.key === activeKey}
              onClick={onNodeClick}
              style={{
                position: "absolute",
                left: p.x,
                top: p.y,
                width: NODE_W,
                height: NODE_H,
              }}
            />
          );
        })
      )}
    </>
  );

  return (
    <div className={`ms-canvas${isEdit ? " ms-canvas--edit" : ""}`}>
      {(title || subtitle) && (
        <div className="ms-canvas-header">
          {title && <span className="ms-canvas-heading">{title}</span>}
          {subtitle && <span className="ms-canvas-subheading">{subtitle}</span>}
        </div>
      )}

      {isEdit && !hasNodes ? (
        <div className="ms-canvas-empty">
          <p className="ms-canvas-empty-hint">
            Start your workflow — pick a step type
          </p>
          <div className="ms-canvas-empty-grid">
            {PRESET_TILES.map((t) => (
              <button
                key={t.label}
                type="button"
                className="ms-canvas-tile"
                onClick={() => addPreset(t.preset)}
              >
                <span className="ms-canvas-tile-icon">{t.icon}</span>
                <span className="ms-canvas-tile-label">{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="ms-canvas-viewport">
          <div
            className={[
              "ms-canvas-scroll",
              // Centre only while the tree FITS. Centring an overflowing flex container
              // makes its leading edge unreachable by scrolling — a well-known trap —
              // so when it overflows the content starts at the origin and you pan.
              fits ? "ms-canvas-scroll--fits" : "ms-canvas-scroll--pannable",
              panning ? "ms-canvas-scroll--panning" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            ref={scrollRef}
            onMouseLeave={clearHover}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPan}
            onPointerCancel={endPan}
          >
            <div
              className="ms-canvas-sizer"
              style={{ width: stageW * scale, height: stageH * scale }}
            >
              <div
                className="ms-canvas-stage"
                style={{
                  width: stageW,
                  height: stageH,
                  transform: `scale(${scale})`,
                  transformOrigin: "0 0",
                }}
              >
                {stageInner}
              </div>
            </div>
          </div>
          {/* Pinned to the VIEWPORT, not the node layer — a sibling of the zoom overlay on
              the opposite corner. Being outside .ms-canvas-scroll is what keeps it in the
              same screen position through pan, zoom and node drags. */}
          {isEdit && (
            <button
              type="button"
              className="ms-canvas-addfloat"
              onClick={onAddStep}
              title="Add a step at the end of the workflow"
            >
              <PlusOutlined />
              <span>Add step</span>
            </button>
          )}
          {controls}
        </div>
      )}
    </div>
  );
}

export default MasterSuiteFlow;
